import { type PmDoc, type PmInline, PmNodeType } from "@storytell/types/richText";

export const parseStringIntoJSONContent = (
  content: string | PmDoc,
  opts?: {
    extractAssets?: { id: string; name: string }[];
    extractConcepts?: { id: string; name: string }[];
    extractLabels?: { id: string; name: string }[];
    extractViews?: { id: string; name: string }[];
  },
) => {
  if (typeof content !== "string") return content;
  const parts = content.split("\n").filter((p) => p.trim() !== "");
  const jsonContent: PmDoc = {
    type: PmNodeType.Doc,
    content: [],
  };
  for (const part of parts) {
    jsonContent.content?.push({
      type: PmNodeType.Paragraph,
      content: splitContentIntoParts(part, opts),
    });
  }
  return jsonContent;
};

const splitContentIntoParts = (
  content: string | undefined,
  opts?: {
    extractAssets?: { id: string; name: string }[];
    extractConcepts?: { id: string; name: string }[];
    extractLabels?: { id: string; name: string }[];
    extractViews?: { id: string; name: string }[];
  },
): PmInline[] => {
  if (!content) return [];
  if (!opts) return [{ type: PmNodeType.Text, text: content }];

  const idToName = new Map<string, string>();
  const nameToId = new Map<string, string>();
  const nameToType = new Map<string, "asset" | "concept" | "label" | "view">();

  for (const asset of opts?.extractAssets || []) {
    idToName.set(asset.id, asset.name);
    nameToId.set(asset.name, asset.id);
    nameToType.set(asset.name, "asset");
  }
  for (const concept of opts?.extractConcepts || []) {
    idToName.set(concept.id, concept.name);
    nameToId.set(concept.name, concept.id);
    nameToType.set(concept.name, "concept");
  }
  for (const label of opts?.extractLabels || []) {
    idToName.set(label.id, label.name);
    nameToId.set(label.name, label.id);
    nameToType.set(label.name, "label");
  }
  for (const view of opts?.extractViews || []) {
    idToName.set(view.id, view.name);
    nameToId.set(view.name, view.id);
    nameToType.set(view.name, "view");
  }

  // Build a regex from all asset and concept names, matching each name by itself or in single/double quotes
  const allNames = [
    ...(opts?.extractAssets?.map((a) => a.name) || []),
    ...(opts?.extractConcepts?.map((c) => c.name) || []),
    ...(opts?.extractLabels?.map((l) => l.name) || []),
    ...(opts?.extractViews?.map((v) => v.name) || []),
  ].filter(Boolean);

  if (allNames.length === 0) {
    return [{ type: PmNodeType.Text, text: content }];
  }

  // Escape regex special characters in names
  const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Sort by length descending to match longer names first (avoid partial matches)
  const sortedNames = [...allNames].sort((a, b) => b.length - a.length);
  const namePattern = sortedNames.map(escapeRegex).join("|");
  // Match: "name", 'name', or name (not inside quotes)
  const regex = new RegExp(`"(${namePattern})"|'(${namePattern})'|\\b(${namePattern})\\b`, "g");

  const parts: PmInline[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while (true) {
    match = regex.exec(content);
    if (match === null) break;

    // Add text before the match
    if (match.index > lastIndex) {
      const textBefore = content.slice(lastIndex, match.index);
      if (textBefore) {
        parts.push({ type: PmNodeType.Text, text: textBefore });
      }
    }

    // match[1] is double-quoted, match[2] is single-quoted, match[3] is unquoted
    const name = match[1] || match[2] || match[3];
    if (name && nameToId.has(name)) {
      const id = nameToId.get(name)!;
      const type = nameToType.get(name)!;
      parts.push({
        type: PmNodeType.VariableV1,
        attrs: {
          type,
          id,
          text: name,
        },
      });
    }

    lastIndex = regex.lastIndex;
  }

  // Add remaining text after the last match
  if (lastIndex < content.length) {
    const remainingText = content.slice(lastIndex);
    if (remainingText) {
      parts.push({ type: PmNodeType.Text, text: remainingText });
    }
  }

  return parts.length > 0 ? parts : [{ type: PmNodeType.Text, text: content }];
};
